import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Heart, Home, UserPlus, Sparkles, 
  TrendingUp, Check, Plus, ChevronLeft
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface PulseEntry {
  id: string;
  checkin_date: string;
  partner_connection: number | null;
  family_harmony: number | null;
  friend_support: number | null;
  self_compassion: number | null;
  community_belonging: number | null;
  relational_wellbeing_score: number | null;
  created_at: string;
}

interface SliderDimension {
  key: keyof Pick<PulseEntry, 'partner_connection' | 'family_harmony' | 'friend_support' | 'self_compassion' | 'community_belonging'>;
  label: string;
  description: string;
  icon: React.ElementType;
}

const DIMENSIONS: SliderDimension[] = [
  { 
    key: 'partner_connection', 
    label: 'Partner Connection', 
    description: 'How connected do you feel to your partner or closest intimate relationship?',
    icon: Heart 
  },
  { 
    key: 'family_harmony', 
    label: 'Family Harmony', 
    description: 'How harmonious do your family relationships feel right now?',
    icon: Home 
  },
  { 
    key: 'friend_support', 
    label: 'Friend Support', 
    description: 'How supported do you feel by your friendships?',
    icon: UserPlus 
  },
  { 
    key: 'self_compassion', 
    label: 'Self-Compassion', 
    description: 'How kind and connected do you feel toward yourself?',
    icon: Sparkles 
  },
  { 
    key: 'community_belonging', 
    label: 'Community Belonging', 
    description: 'How much do you feel you belong to a wider community or group?',
    icon: Users 
  },
];

const DATE_RANGES = [
  { value: '14', label: 'Last 2 weeks' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
];

interface RelationshipPulseCheckerProps {
  onBack?: () => void;
}

export function RelationshipPulseChecker({ onBack }: RelationshipPulseCheckerProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  const [pulseHistory, setPulseHistory] = useState<PulseEntry[]>([]);
  const [hasSavedToday, setHasSavedToday] = useState(false);
  
  // Form state
  const [formValues, setFormValues] = useState({
    partner_connection: 5,
    family_harmony: 5,
    friend_support: 5,
    self_compassion: 5,
    community_belonging: 5,
  });

  // Calculate average for display
  const calculatedScore = Math.round(
    (formValues.partner_connection + 
     formValues.family_harmony + 
     formValues.friend_support + 
     formValues.self_compassion + 
     formValues.community_belonging) / 5 * 10
  ) / 10;

  // Fetch pulse history
  useEffect(() => {
    const fetchPulseHistory = async () => {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('relationship_pulse_checkins')
        .select('*')
        .eq('user_id', user.id)
        .gte('checkin_date', startDate)
        .order('checkin_date', { ascending: true });

      if (!error && data) {
        setPulseHistory(data);
        // Check if user already logged today
        const todayEntry = data.find(entry => entry.checkin_date === today);
        setHasSavedToday(!!todayEntry);
      }
      
      setIsLoading(false);
    };

    fetchPulseHistory();
  }, [dateRange]);

  const handleSliderChange = (key: keyof typeof formValues, value: number[]) => {
    setFormValues(prev => ({ ...prev, [key]: value[0] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Not signed in",
        description: "Please sign in to save your pulse check.",
        variant: "destructive"
      });
      setIsSaving(false);
      return;
    }

    const today = format(new Date(), 'yyyy-MM-dd');
    
    const { error } = await supabase
      .from('relationship_pulse_checkins')
      .upsert({
        user_id: user.id,
        checkin_date: today,
        partner_connection: formValues.partner_connection,
        family_harmony: formValues.family_harmony,
        friend_support: formValues.friend_support,
        self_compassion: formValues.self_compassion,
        community_belonging: formValues.community_belonging,
        relational_wellbeing_score: calculatedScore,
      }, {
        onConflict: 'user_id,checkin_date'
      });

    if (error) {
      toast({
        title: "Error saving",
        description: "There was a problem saving your pulse check. Please try again.",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Pulse check saved",
        description: "Thank you for checking in. This doesn't need to be perfect - it's simply information we can use over time."
      });
      setShowForm(false);
      setHasSavedToday(true);
      // Refresh data
      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      const { data: refreshedData } = await supabase
        .from('relationship_pulse_checkins')
        .select('*')
        .eq('user_id', user.id)
        .gte('checkin_date', startDate)
        .order('checkin_date', { ascending: true });
      
      if (refreshedData) {
        setPulseHistory(refreshedData);
      }
    }
    
    setIsSaving(false);
  };

  // Prepare chart data
  const chartData = pulseHistory.map(entry => ({
    date: format(new Date(entry.checkin_date), 'MMM d'),
    'Overall': entry.relational_wellbeing_score || 0,
    'Partner': entry.partner_connection || 0,
    'Family': entry.family_harmony || 0,
    'Friends': entry.friend_support || 0,
    'Self': entry.self_compassion || 0,
    'Community': entry.community_belonging || 0,
  }));

  const getScoreLabel = (score: number): string => {
    if (score >= 8) return 'Strong connection';
    if (score >= 6) return 'Moderate connection';
    if (score >= 4) return 'Some distance';
    return 'Feeling disconnected';
  };

  const getScoreColor = (score: number): string => {
    if (score >= 8) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 6) return 'text-primary';
    if (score >= 4) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Back Button */}
      {onBack && (
        <Button
          variant="ghost"
          onClick={onBack}
          className="hover:bg-primary/10 hover:text-primary mb-2"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Insights
        </Button>
      )}

      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Relational Pulse Check
          </CardTitle>
          <CardDescription>
            A gentle snapshot of how connected you've been feeling - with yourself and others.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!showForm ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {pulseHistory.length > 0 && (
                  <div className="text-center">
                    <div className={`text-3xl font-light ${getScoreColor(pulseHistory[pulseHistory.length - 1]?.relational_wellbeing_score || 0)}`}>
                      {pulseHistory[pulseHistory.length - 1]?.relational_wellbeing_score?.toFixed(1) || '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Latest score</div>
                  </div>
                )}
              </div>
              <Button 
                onClick={() => setShowForm(true)}
                disabled={hasSavedToday}
                className="gap-2"
              >
                {hasSavedToday ? (
                  <>
                    <Check className="w-4 h-4" />
                    Logged Today
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    New Pulse Check
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {DIMENSIONS.map((dimension) => {
                const Icon = dimension.icon;
                const value = formValues[dimension.key];
                return (
                  <div key={dimension.key} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{dimension.label}</span>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(value)}`}>
                        {value}/10
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{dimension.description}</p>
                    <Slider
                      value={[value]}
                      min={0}
                      max={10}
                      step={1}
                      onValueChange={(v) => handleSliderChange(dimension.key, v)}
                      className="w-full"
                    />
                  </div>
                );
              })}

              {/* Calculated Score Display */}
              <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">Overall Relational Wellbeing</span>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-light ${getScoreColor(calculatedScore)}`}>
                        {calculatedScore.toFixed(1)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {getScoreLabel(calculatedScore)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving} className="flex-1">
                  {isSaving ? 'Saving...' : 'Save Pulse Check'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trend Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Relational Wellbeing Over Time
              </CardTitle>
              <CardDescription className="text-xs">
                Track how your connections evolve
              </CardDescription>
            </div>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {pulseHistory.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    domain={[0, 10]}
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="Overall" 
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 0, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Partner" 
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Family" 
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Friends" 
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Self" 
                    stroke="hsl(var(--chart-4))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Community" 
                    stroke="hsl(var(--chart-5))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center">
              <div className="text-center p-6 rounded-lg bg-muted/30 max-w-md">
                <Users className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground mb-2">
                  You haven't logged any relational pulses yet.
                </p>
                <p className="text-xs text-muted-foreground">
                  When you're ready, you can start by simply noticing how connected or alone you've been feeling recently.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reflection Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Heart className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h4 className="text-sm font-medium mb-1">About This Tool</h4>
              <p className="text-xs text-muted-foreground">
                This pulse check is a simple, compassionate way to notice your relational patterns over time. 
                There are no right or wrong answers - only information that may help you understand yourself better. 
                Your sense of connection naturally fluctuates, and tracking it can reveal patterns you might not otherwise see.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
